/** Above this length the body is likely a stack/HTML dump, not a user message. */
const MAX_MESSAGE_LENGTH = 200;

/**
 * The API refused the request inside a 2xx. Multi-status endpoints (`POST /tickets/refund` answers 207 for
 * every per-ticket refusal; a malformed body or a pending-upgrade hold is still a plain 4xx) report the refusal
 * in the body's `errors[]`, not in the status code, so a mutation throws this (via `throwIfRefused`) to take
 * its error path. `message` is the API's reason, '' when it gave none.
 */
export class RefusedByApiError extends Error {
	constructor(reason = '') {
		super(reason);
		this.name = 'RefusedByApiError';
	}
}

/** One refusal in a multi-status body. `error` is the API's untagged Go `ErrorResponse`, so its text sits
 *  under a capitalised `Error` key (the key the green Scanner's `ScanError` reads, `PunchupPages/Scanner/types.ts`). */
interface MultiStatusRefusal {
	error?: { Error?: unknown };
}

/** A Stripe failure arrives JSON-encoded with a human `message`; a plain-text reason is kept as-is. */
const readableReason = (raw: string): string => {
	try {
		const { message } = JSON.parse(raw) as { message?: unknown };
		return typeof message === 'string' ? message : raw;
	} catch {
		// Not a JSON object (the usual plain-text refusal): the raw string is the reason.
		return raw;
	}
};

/**
 * Passes a multi-status body through when it carries no refusals, and throws a `RefusedByApiError` with the
 * first refusal's reason when it does. Chain it onto the request (`post(...).then(throwIfRefused)`) so a
 * refused 2xx reaches the mutation's error path instead of its success toast. For single-entity requests;
 * a batch must also read the success list.
 */
export const throwIfRefused = <T>(body: T): T => {
	const refusals = (body as { errors?: unknown } | undefined)?.errors;
	if (!Array.isArray(refusals) || refusals.length === 0) return body;
	const raw = (refusals[0] as MultiStatusRefusal | null)?.error?.Error;
	throw new RefusedByApiError(typeof raw === 'string' ? readableReason(raw) : '');
};

/**
 * Extracts the human-readable message the API returned for a failed mutation.
 * The ticketing-api error responses serialise the `ErrorResponse.Error` field
 * as a bare JSON string (e.g. `ctx.IndentedJSON(err.Status, err.Error)`), so
 * `error.response.data` is usually the message itself; some endpoints wrap it
 * as `{ error }` / `{ message }`. A `RefusedByApiError`'s reason is vetted the
 * same way as a bare-string body. Falls back to the provided default when no
 * usable message is present, so the caller can keep a safe generic toast.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
	const data =
		error instanceof RefusedByApiError ? error.message : (error as { response?: { data?: unknown } })?.response?.data;
	let message: string | undefined;
	if (typeof data === 'string' && data.trim()) message = data;
	else if (data && typeof data === 'object') {
		const body = data as { error?: unknown; message?: unknown };
		if (typeof body.error === 'string' && body.error.trim()) message = body.error;
		else if (typeof body.message === 'string' && body.message.trim()) message = body.message;
	}
	if (message && message.length <= MAX_MESSAGE_LENGTH) return message;
	return fallback;
};
